import { Suspense, useMemo, lazy } from 'react';
import { Route } from 'react-router-dom';
import { TracedRoutes } from '@comeon/error-reporter';
import * as Paths from '~/components/router/constants.js';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import ProductNavbar from '~/components/header/product-navbar/product-navbar-async.js';
import { ROUTE_WE_SPIN_GAME_VIEW } from '~/components/we-spin/new-flow/constants/we-spin-route-constants.js';
import { useSelector } from 'react-redux';
import HeaderSelector from '~/components/header/header-selector.js';
import { isSmallSelector, Smaller } from '~/components/layout/mediaquery';
import NullLoader from '~/components/null-loader.js';
import ScrollAdapt from '~/components/misc/scroll-adapt.js';
import styled from 'styled-components';
import { getZindexByType } from '~/utils/style-utils.ts';
import { selectPendingBets } from '~/components/highlight-games/ducks/highlight-games-duck';
import { isNativeBottomNavigationEnabled } from '~/components/app-bridge/app-properties.js';

const BottomNavigation = lazy(
	() =>
		import(
			/* webpackChunkName: "global-navigation" */ '~/components/global-navigation/bottom-navigation/bottom-navigation'
		)
);

const StickyFooter = styled(ScrollAdapt)`
	z-index: ${getZindexByType('small-product-nav')};
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
`;

const RoutesSubheader = () => {
	const smaller = useSelector(isSmallSelector);
	const isHGBetslipWithNoSelections = useSelector(selectPendingBets);
	const hasNativeBottomNavigation = isNativeBottomNavigationEnabled();

	const casinoRoutes = useCasinoRoutes();

	/* FIXME: do we need this ??
	RoutesSubheader is mounted when hasSubheader is true, doesnt that take care of this?
	Seems like if we add this, Product navbar is not mounted */
	const SUB_HEADER_PATHS = useMemo(
		() => [
			casinoRoutes.ROUTE_CASINO,
			casinoRoutes.ROUTE_LIVE_CASINO,
			Paths.ROUTE_SPORTSBOOK,
			Paths.ROUTE_LIVEBETTING,
			Paths.ROUTE_SHOP,
			Paths.ROUTE_PROMOTIONS,
			Paths.ROUTE_ABOUT,
			Paths.ROUTE_TERMSANDCONDITIONS,
			Paths.ROUTE_PRIVACYPOLICY,
			Paths.ROUTE_RESPONSIBLEGAMING,
			Paths.ROUTE_BONUS,
			Paths.ROUTE_TVBET,
			Paths.ROUTE_USERFEEDBACK,
			Paths.ROUTE_WE_SPIN,
			Paths.ROUTE_FREEBET,
			Paths.ROUTE_LEGAL_BETTING,
			Paths.ROUTE_BETTING_TIPS,
			Paths.ROUTE_TAX,
			Paths.ROUTE_THUNDERBITE,
			Paths.ROUTE_SPLASH_TECH,
			Paths.ROUTE_VERIFICATION_POLAND,
			Paths.ROUTE_PAYMENTS_POLAND,
			Paths.ROUTE_VIRTUALS,
			Paths.ROUTE_FREE_TO_PLAY,
			Paths.ROUTE_FREE_TO_PLAY_CUSTOM
		],
		[casinoRoutes.ROUTE_CASINO, casinoRoutes.ROUTE_LIVE_CASINO]
	);

	const ROUTES_WITH_NO_SUBHEADER = useMemo(
		() => [casinoRoutes.ROUTE_CASINO_GAME, casinoRoutes.ROUTE_LIVE_CASINO_GAME, ROUTE_WE_SPIN_GAME_VIEW],
		[casinoRoutes.ROUTE_CASINO_GAME, casinoRoutes.ROUTE_LIVE_CASINO_GAME]
	);

	return (
		<>
			<TracedRoutes>
				{/* Common sub header  */}
				{SUB_HEADER_PATHS.map(path => (
					<Route
						path={`${path}/*`}
						key={path}
						element={
							<Suspense fallback={<NullLoader />}>
								<ProductNavbar />
							</Suspense>
						}
					/>
				))}
				{ROUTES_WITH_NO_SUBHEADER.map(path => (
					<Route path={`${path}/*`} key={path} />
				))}
				<Route path="*" element={<NullLoader />} />
			</TracedRoutes>
			<TracedRoutes>
				{/* Product specific sub header and navigation elements  */}
				<Route
					path={Paths.ROUTE_HIGHLIGHT_GAMES}
					element={
						<Smaller>
							<StickyFooter disableInlineStyles>
								<HeaderSelector
									isHGBetslipWithNoSelections={isHGBetslipWithNoSelections.length === 0}
								/>
							</StickyFooter>
						</Smaller>
					}
				/>
				{smaller &&
					!hasNativeBottomNavigation &&
					[casinoRoutes.ROUTE_CASINO, casinoRoutes.ROUTE_LIVE_CASINO, Paths.ROUTE_SPORTSBOOK].map(path => (
						<Route
							path={`${path}/*`}
							key={path}
							element={
								<Suspense fallback={<NullLoader />}>
									<BottomNavigation />
								</Suspense>
							}
						/>
					))}
				{smaller &&
					[Paths.ROUTE_SHOP, Paths.ROUTE_PROMOTIONS, Paths.ROUTE_WE_SPIN].map(path => (
						<Route
							path={`${path}/*`}
							key={path}
							element={
								<StickyFooter disableInlineStyles>
									<HeaderSelector />
								</StickyFooter>
							}
						/>
					))}
				<Route path="*" element={<NullLoader />} />
			</TracedRoutes>
			<TracedRoutes>
				{/* Add CTA on top of bottom-nav  */}
				{smaller &&
					!hasNativeBottomNavigation &&
					[casinoRoutes.ROUTE_CASINO, casinoRoutes.ROUTE_LIVE_CASINO].map(path => (
						<Route path={`${path}/*`} key={path} element={<HeaderSelector hasBottomOffset />} />
					))}
			</TracedRoutes>
		</>
	);
};

export default RoutesSubheader;
